/**
 * menu-view-upserts.ts — czysta logika zapisu nazwanych layoutów razem z menu szablonu
 * (wyciągnięta z `MenuBuilderEmbedded` do testowalnych helperów, fix 2026-07-20).
 *
 * Dwa kontrakty:
 *  - `buildMenuViewUpserts` — które widoki z `localEntities` jadą w PATCH menu,
 *  - `collectDanglingMenuRefLabels` — lustro serwerowego `collectViewRefProblems`
 *    (apps/api/lib/module-template-menu/views.ts) na stanie LOKALNYM buildera, zwracające
 *    etykiety pozycji z wiszącym `viewRef`/`listRef` — user dostaje konkretną pozycję do
 *    naprawy zamiast generycznego „Nie udało się zapisać menu" po serwerowym 422.
 */

import type { MenuItem, ModuleTemplateEntityDefinition } from '@saas/shared';

import type { TemplateMenuViewUpsert } from '@/lib/module-templates/template-layouts';

/**
 * Nazwane layouty (formularz i lista) do zapisu razem z menu. Wysyłamy WSZYSTKIE widoki
 * nazwane (isDefault=false) — domyślne widoki żyją w strukturze i nie są tu ruszane.
 * Backend upsertuje je w entity.views / bibliotece kartoteki.
 *
 * Widok form bez configu (utworzony, ale układ jeszcze nie zapisany w builderze) jedzie
 * z PUSTYM układem zamiast być po cichu wycięty — wycięcie zostawiało wiszący `viewRef`
 * pozycji menu i cały zapis padał na serwerze (422 VIEW_REF_NOT_FOUND, bug 2026-07-20).
 * Widok list bez configu zostaje pominięty jak dotąd (pusta lista bez kolumn i tak nie
 * przejdzie walidacji serwera; ścieżki tworzenia list zawsze seedują config).
 */
export function buildMenuViewUpserts(
  entities: ModuleTemplateEntityDefinition[],
): TemplateMenuViewUpsert[] {
  return entities.flatMap((e) => {
    const formViews: TemplateMenuViewUpsert[] = e.views
      .filter((v) => v.type === 'form' && !v.isDefault)
      .map((v) => ({
        viewType: 'form' as const,
        entitySlug: e.slug,
        slug: v.slug,
        name: v.name,
        formConfig: v.formConfig ?? { elements: [] },
        sortOrder: v.sortOrder,
      }));
    const listViews: TemplateMenuViewUpsert[] = e.views
      .filter((v) => v.type === 'list' && !v.isDefault && v.listConfig)
      .map((v) => ({
        viewType: 'list' as const,
        entitySlug: e.slug,
        slug: v.slug,
        name: v.name,
        listConfig: v.listConfig!,
        sortOrder: v.sortOrder,
      }));
    return [...formViews, ...listViews];
  });
}

/**
 * Zwraca ZDEDUPLIKOWANE etykiety pozycji menu, których `viewRef` (widok form) lub
 * `listRef` (widok list) nie wskazuje żadnego widoku istniejącego w encjach buildera.
 * Pusta tablica = spójnie, można wysyłać PATCH.
 */
export function collectDanglingMenuRefLabels(
  hierarchy: MenuItem[],
  entities: ModuleTemplateEntityDefinition[],
  /**
   * Kartoteki, których układów nie znamy (biblioteka jeszcze się wczytuje) — ich wskaźników
   * nie oceniamy lokalnie (sprawdzi je serwer), żeby nie blokować zapisu fałszywym alarmem.
   */
  skipModuleCodes?: ReadonlySet<string>,
): string[] {
  const formKeys = new Set<string>();
  const listKeys = new Set<string>();
  for (const e of entities) {
    for (const v of e.views) {
      (v.type === 'form' ? formKeys : listKeys).add(`${e.slug}:${v.slug}`);
    }
  }
  const labels: string[] = [];
  const walk = (items: MenuItem[]): void => {
    for (const item of items) {
      const label = item.label || item.moduleCode || item.id;
      if (item.moduleCode && skipModuleCodes?.has(item.moduleCode)) {
        if (item.children?.length) walk(item.children);
        continue;
      }
      if (item.viewRef && !formKeys.has(item.viewRef)) labels.push(label);
      if (item.listRef && !listKeys.has(item.listRef)) labels.push(label);
      if (item.children?.length) walk(item.children);
    }
  };
  walk(hierarchy);
  return [...new Set(labels)];
}
